import {
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { TbSearch } from "react-icons/tb";
import Input from "../../component/common/Input/Input";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllUsersJobsAsync } from "../../services/UserJobsServices";
import { get_users_list_by_role } from "../../services/DashboardUsers";
import { get_all_conversations } from "../../services/ConversationsService";
import { asyncStatus } from "../../utils/asyncStatus";
import { themeOrange } from "../../utils/colorTheme";

const DashboardMessage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get orders data from user jobs
  const { get_all_user_jobs_status, get_all_user_jobs_data } = useSelector(
    (state) => state.userJobsSlice
  );

  // Get vendors list
  const { get_users_list_by_role_data } = useSelector(
    (state) => state.dashboard_users
  );

  // Get conversation list (union of orders + message-only pairs from backend)
  const { get_conversations_data } = useSelector(
    (state) => state.conversations
  );

  useEffect(() => {
    dispatch(getAllUsersJobsAsync());
    dispatch(get_users_list_by_role(2)); // Fetch vendors (role 2)
    dispatch(get_all_conversations());
  }, [dispatch]);

  // Create a vendor lookup map
  const vendorMap = useMemo(() => {
    const map = new Map();
    if (get_users_list_by_role_data && Array.isArray(get_users_list_by_role_data)) {
      get_users_list_by_role_data.forEach(vendor => {
        map.set(vendor.id, vendor);
      });
    }
    return map;
  }, [get_users_list_by_role_data]);

  // Build conversations from orders
  const conversationsData = useMemo(() => {
    if (!get_all_user_jobs_data || vendorMap.size === 0) {
      return [];
    }

    const ordersArray = Array.isArray(get_all_user_jobs_data)
      ? get_all_user_jobs_data
      : [];

    if (ordersArray.length === 0) {
      return [];
    }

    const conversationMap = new Map();

    ordersArray.forEach((order) => {
      const user = order?.user;
      // Try to get vendor from order directly, or lookup by vendor_id
      let vendor = order?.vendor || order?.assigned_vendor;

      // If no vendor object but has vendor_id, lookup from vendorMap
      if (!vendor && order?.vendor_id) {
        vendor = vendorMap.get(order.vendor_id);
      }

      if (user && vendor && (vendor.id || vendor.id === 0)) {
        const key = `${user.id}-${vendor.id}`;
        const existingConv = conversationMap.get(key);

        if (!existingConv || new Date(order.updated_at) > new Date(existingConv.latest_order?.updated_at || 0)) {
          conversationMap.set(key, {
            id: key,
            user: user,
            vendor: vendor,
            latest_order: {
              id: order.id,
              status: order.status,
              service_name: order.service?.name || order.service_name || "Service",
              updated_at: order.updated_at || order.created_at,
            },
          });
        }
      }
    });

    const fromOrders = Array.from(conversationMap.values());
    // If backend returned the full union list (orders + message-only pairs), prefer it
    if (Array.isArray(get_conversations_data) && get_conversations_data.length >= fromOrders.length) {
      return get_conversations_data;
    }
    return fromOrders;
  }, [get_all_user_jobs_data, vendorMap, get_conversations_data]);

  const isLoading = get_all_user_jobs_status === asyncStatus.LOADING;

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const trimmedSearch = searchTerm.trim().toLowerCase();

  // Filter conversations by search
  const filteredConversations = conversationsData.filter(
    (conv) =>
      !trimmedSearch ||
      conv?.user?.first_name?.toLowerCase()?.includes(trimmedSearch) ||
      conv?.user?.last_name?.toLowerCase()?.includes(trimmedSearch) ||
      conv?.vendor?.first_name?.toLowerCase()?.includes(trimmedSearch) ||
      conv?.vendor?.last_name?.toLowerCase()?.includes(trimmedSearch) ||
      conv?.user?.email?.toLowerCase()?.includes(trimmedSearch) ||
      conv?.vendor?.email?.toLowerCase()?.includes(trimmedSearch)
  );

  const handleViewConversation = (conv) => {
    navigate("/message/chat-screen", {
      state: {
        userId: conv.user.id,
        vendorId: conv.vendor.id,
        userName: `${conv.user.first_name} ${conv.user.last_name}`,
        vendorName: `${conv.vendor.first_name} ${conv.vendor.last_name}`,
        userAvatar: conv.user.avatar,
        vendorAvatar: conv.vendor.avatar,
      },
    });
  };

  return (
    <Stack bgcolor={"#FAFAFA"} gap={3}>
      <Stack
        direction={{
          xl: "row",
          lg: "row",
          md: "row",
          sm: "row",
          xs: "column",
        }}
        alignItems={{
          xl: "center",
          lg: "center",
          md: "center",
          sm: "center",
          xs: "flex-start",
        }}
        justifyContent={"space-between"}
        gap={1}
        my={1}
        flexWrap={"wrap"}
      >
        <Typography className="mainHeading">User-Pro Conversations</Typography>

        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={2}
          sx={{ height: "40px" }}
        >
          <Input
            id={"search_field"}
            onChange={handleSearch}
            placeholder={"Search by name or email..."}
            value={searchTerm}
            style={{
              border: "1px solid #D8D8D8",
              borderRadius: "10px",
              paddingLeft: "10px",
              height: "100%",
              width: "320px",
              marginTop: "0px",
            }}
          />
          <IconButton
            sx={{
              border: "1px solid lightgrey",
              p: 1,
              height: "100%",
              borderRadius: "10px",
            }}
          >
            <TbSearch style={{ color: "#656565", fontSize: "20px" }} />
          </IconButton>
        </Stack>
      </Stack>
      <hr color={"#D1D1D1"} />

      <Container
        sx={{
          maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" },
        }}
      >
        {isLoading ? (
          <Stack
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ height: "40vh" }}
          >
            <CircularProgress size={30} sx={{ color: themeOrange }} />
          </Stack>
        ) : filteredConversations.length > 0 ? (
          <Stack gap={2}>
            {filteredConversations.map((conv) => (
              <Box
                key={conv.id}
                onClick={() => handleViewConversation(conv)}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  padding: "16px 20px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: "1px solid #eee",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    transform: "translateY(-2px)",
                    borderColor: themeOrange,
                  },
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                >
                  {/* User Info */}
                  <Stack direction="row" alignItems="center" gap={2} flex={1}>
                    <Avatar
                      src={conv.user.avatar}
                      sx={{ width: 48, height: 48 }}
                    />
                    <Box>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: "15px", color: "#333" }}
                      >
                        {conv.user.first_name} {conv.user.last_name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#888" }}>
                        Customer
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Arrow */}
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ px: 2 }}
                  >
                    <Typography
                      sx={{
                        fontSize: "20px",
                        color: themeOrange,
                        fontWeight: "bold",
                      }}
                    >
                      ↔
                    </Typography>
                  </Stack>

                  {/* Vendor Info */}
                  <Stack direction="row" alignItems="center" gap={2} flex={1}>
                    <Avatar
                      src={conv.vendor.avatar}
                      sx={{ width: 48, height: 48 }}
                    />
                    <Box>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: "15px", color: "#333" }}
                      >
                        {conv.vendor.first_name} {conv.vendor.last_name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#888" }}>
                        Pro
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Order Info */}
                  {conv.latest_order && (
                    <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                      <Chip
                        label={conv.latest_order.status || "Unknown"}
                        size="small"
                        sx={{
                          backgroundColor:
                            conv.latest_order.status === "Completed"
                              ? "#e8f5e9"
                              : conv.latest_order.status === "Assigned"
                              ? "#e3f2fd"
                              : conv.latest_order.status === "In-Queue"
                              ? "#fff3e0"
                              : "#f5f5f5",
                          color:
                            conv.latest_order.status === "Completed"
                              ? "#2e7d32"
                              : conv.latest_order.status === "Assigned"
                              ? "#1565c0"
                              : conv.latest_order.status === "In-Queue"
                              ? "#ef6c00"
                              : "#666",
                          fontWeight: 600,
                          fontSize: "11px",
                        }}
                      />
                      {conv.latest_order.service_name && (
                        <Typography sx={{ fontSize: "12px", color: "#666" }}>
                          {conv.latest_order.service_name}
                        </Typography>
                      )}
                      {conv.latest_order.updated_at && (
                        <Typography sx={{ fontSize: "11px", color: "#999" }}>
                          {new Date(conv.latest_order.updated_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : get_all_user_jobs_status === asyncStatus.ERROR ? (
          <Stack
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ height: "40vh" }}
          >
            <Typography sx={{ color: "#c62828", mb: 2 }}>
              Failed to load data. Please try logging in again.
            </Typography>
            <Box
              onClick={() => window.location.href = "/"}
              sx={{
                backgroundColor: themeOrange,
                color: "#fff",
                padding: "10px 24px",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Go to Login
            </Box>
          </Stack>
        ) : (
          <Stack
            alignItems={"center"}
            justifyContent={"center"}
            sx={{ height: "40vh" }}
            gap={2}
          >
            <Typography sx={{ color: "#888" }}>
              {searchTerm ? "No conversations match your search" : "No conversations found"}
            </Typography>
            <Typography sx={{ color: "#aaa", fontSize: "14px" }}>
              Conversations appear here when orders have both a customer and an assigned vendor.
            </Typography>
          </Stack>
        )}
      </Container>
    </Stack>
  );
};

export default DashboardMessage;
